import React from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { Shield, Mail, LogIn, ChevronRight, Smartphone, ShoppingCart } from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import jwtDecode from 'jwt-decode';
import { motion } from 'framer-motion';

import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { ANDROID_APK_PUBLIC_PATH, getRouterBasename } from '../utils/routerBasename';
import { APP_VERSION, BUILD_DATE } from '../config/constants';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, loading } = useAuth();
  const [error, setError] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('reason') === 'session') {
      setError('Your session expired or is no longer valid. Please sign in with Google again.');
    }
  }, []);

  const handleGoogleSuccess = React.useCallback(async (credentialResponse) => {
    if (!credentialResponse?.credential) {
      setError('Google did not return a sign-in token. Please try again.');
      return;
    }
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.post('/api-v1/auth/google-login', {
        credential: credentialResponse.credential,
      });

      if (response.data.success) {
        const { token, user } = response.data;

        login({
          ...user,
          name: user?.name || user?.email?.split('@')[0] || 'User',
          role: user?.role || 'admin',
          token,
          provider: 'google',
        });
        const base = getRouterBasename();
        window.location.replace(`${window.location.origin}${base}/overview`);
      } else {
        setError(response.data.message || 'Verification failed');
      }
    } catch (err) {
      console.error('[Login] Auth Bridge Error:', err);
      setError(err.response?.data?.message || 'Server authentication failed.');
    } finally {
      setIsLoading(false);
    }
  }, [login]);

  // After Google redirect POST → /login, server stashes credential and sends us here
  React.useEffect(() => {
    const pending = sessionStorage.getItem('qtf_google_credential');
    if (pending) {
      sessionStorage.removeItem('qtf_google_credential');
      handleGoogleSuccess({ credential: pending });
    }
  }, [handleGoogleSuccess]);

  if (loading || isLoading) return (
    <div className="min-h-screen bg-[#0F172A] flex flex-col items-center justify-center gap-4">
      <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      <p className="text-slate-400 text-sm font-medium">Signing you in...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center p-6 relative overflow-hidden font-inter">
      {/* Decorative Blobs */}
      <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-blue-600/20 blur-[120px] rounded-full animate-pulse" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-indigo-600/10 blur-[100px] rounded-full" />

      {/* Build stamp — lets you verify which dashboard build is deployed */}
      <p className="absolute bottom-3 left-0 right-0 text-center text-[11px] text-slate-500 z-10">
        {APP_VERSION} · {BUILD_DATE}
      </p>

      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-[480px] z-10"
      >
        <div className="glass-card bg-white/5 border-white/10 backdrop-blur-2xl p-10 shadow-2xl relative overflow-hidden group">
            {/* Header */}
            <div className="flex flex-col items-center mb-10 text-center">
               <motion.div 
                whileHover={{ rotate: 5, scale: 1.05 }}
                className="w-16 h-16 bg-gradient-to-tr from-blue-600 to-indigo-500 rounded-2xl flex items-center justify-center shadow-2xl shadow-blue-500/20 mb-6"
               >
                 <Shield className="w-8 h-8 text-white" />
               </motion.div>
               <h1 className="text-4xl font-extrabold text-white tracking-tight mb-2">QUARRYFORCE</h1>
               <p className="text-slate-400 font-medium">Internal Command Center Access</p>
            </div>

            {error && (
                <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8 p-4 bg-rose-500/10 border border-rose-500/20 rounded-xl text-rose-400 text-sm font-bold text-center"
                >
                    {error}
                </motion.div>
            )}

            <div className="space-y-6">
                <div className="flex justify-center flex-col items-center">
                    <GoogleLogin
                        onSuccess={handleGoogleSuccess}
                        onError={() => setError('Google sign-in failed. Check OAuth settings in Google Cloud Console.')}
                        theme="filled_black"
                        size="large"
                        shape="pill"
                        useOneTap={false}
                    />
                </div>
            </div>

            {/* Public ordering — no login required */}
            <div className="mt-8 flex flex-col items-center gap-3">
                <div className="flex items-center gap-3 w-full text-slate-500 text-[10px] font-bold uppercase tracking-[0.2em]">
                    <div className="flex-1 h-px bg-white/10" />
                    <span>Are you a customer?</span>
                    <div className="flex-1 h-px bg-white/10" />
                </div>
                <a
                    href={`${window.location.origin}/order`}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-emerald-500/10 border border-emerald-400/30 text-emerald-300 rounded-xl text-sm font-bold hover:bg-emerald-500/20 transition-all group"
                >
                    <ShoppingCart className="w-4 h-4 group-hover:scale-110 transition-transform" />
                    Online Order
                    <ChevronRight className="w-4 h-4 opacity-70 group-hover:translate-x-0.5 transition-transform" />
                </a>
            </div>



            <div className="mt-10 pt-8 border-t border-white/10 flex flex-col items-center gap-4">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em]">Field Force Downloads</p>
                <div className="flex gap-3 w-full">
                    <a 
                        href={ANDROID_APK_PUBLIC_PATH} 
                        download
                        className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-blue-600/10 border border-blue-500/20 text-blue-400 rounded-xl text-xs font-bold hover:bg-blue-600/20 transition-all group"
                    >
                        <Smartphone className="w-4 h-4 group-hover:scale-110 transition-transform" />
                        Android APK
                    </a>
                    <button 
                        disabled
                        className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-white/5 border border-white/5 text-slate-500 rounded-xl text-xs font-bold cursor-not-allowed"
                    >
                        <Shield className="w-4 h-4" />
                        iOS (Soon)
                    </button>
                </div>
            </div>

            <div className="mt-10 flex items-center justify-center gap-6 text-slate-500 font-bold text-[10px] uppercase tracking-widest">
                <span className="flex items-center gap-1.5"><div className="w-1 h-1 bg-emerald-500 rounded-full animate-pulse" /> API SECURE</span>
                <span>v1.0.5</span>
                <span>GLOBAL OPS</span>
            </div>

        </div>

        {/* Info Banner */}
        <div className="mt-8 flex items-center justify-between px-4 text-slate-500 font-bold text-[10px] uppercase tracking-widest">
            <p>© 2024 QUARRYFORCE NETWORK</p>
            <div className="flex gap-4">
                <a href="#" className="hover:text-white transition-colors">Support</a>
                <a href="#" className="hover:text-white transition-colors">Privacy</a>
            </div>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;

